import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Headphones, Phone, Mail, ArrowLeft, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';

const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };
const stagger = { show: { transition: { staggerChildren: 0.1 } } };

const SERVICES = [
  {
    title: 'Flight Booking Assistance',
    desc: 'Need help finding the best flights? Our team will search, compare, and book flights on your behalf at the best available rates.',
    color: 'from-sky-500 to-sky-600',
  },
  {
    title: 'Hotel Reservation Support',
    desc: 'Let us handle your hotel bookings. We negotiate exclusive rates and ensure you get the perfect accommodation for your trip.',
    color: 'from-emerald-500 to-emerald-600',
  },
  {
    title: 'Visa & Documentation Help',
    desc: 'Confused about visa requirements? Our experts guide you through the application process and documentation for any destination.',
    color: 'from-amber-500 to-amber-600',
  },
  {
    title: 'Custom Tour Packages',
    desc: 'Want a tailor-made itinerary? We create personalised tour packages based on your interests, budget, and travel style.',
    color: 'from-violet-500 to-violet-600',
  },
  {
    title: 'Travel Insurance Guidance',
    desc: 'Not sure which insurance to pick? We help you compare plans and choose the right coverage for your destination and activities.',
    color: 'from-rose-500 to-rose-600',
  },
  {
    title: 'Group, Event & Corporate Travel',
    desc: 'Planning a group trip or corporate event? We specialise in coordinating multi-traveller bookings with seamless logistics.',
    color: 'from-teal-500 to-teal-600',
  },
];

export default function AssistedServicesPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="assisted-services-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        <div className="absolute top-32 right-20 w-96 h-96 bg-gold/15 rounded-full blur-3xl animate-pulse" />
      </div>

      <Header />

      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-top">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Headphones className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl text-white font-bold">Assisted Services</h1>
            <p className="text-white/60 text-base mt-2 max-w-xl mx-auto">
              Need personal help? Reach out to our travel experts directly. We're here to assist you with every aspect of your trip.
            </p>
          </motion.div>

          <motion.div initial="hidden" animate="show" variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <motion.div
                key={`service-${i}`}
                variants={fadeUp}
                className="bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 overflow-hidden shadow-lg"
                data-testid={`assisted-card-${i}`}
              >
                <div className={`h-2 bg-gradient-to-r ${s.color}`} />
                <div className="p-5">
                  <h3 className="text-base font-bold text-charcoal mb-2">{s.title}</h3>
                  <p className="text-sm text-charcoal/60 leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Contact Details Section */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-10">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
              <h2 className="text-white font-serif text-2xl text-center mb-6">Get in Touch</h2>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {/* Phone numbers */}
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                    <Phone className="w-6 h-6 text-gold" />
                  </div>
                  <p className="text-white/50 text-xs font-medium uppercase tracking-wider">Call Us</p>
                  <a href="tel:+61401050457" className="text-white hover:text-gold transition-colors text-sm font-semibold" data-testid="contact-phone-1">
                    +61 401 050 457
                  </a>
                  <a href="tel:+61424024078" className="text-white hover:text-gold transition-colors text-sm font-semibold" data-testid="contact-phone-2">
                    +61 424 024 078
                  </a>
                </div>

                {/* Email */}
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                    <Mail className="w-6 h-6 text-gold" />
                  </div>
                  <p className="text-white/50 text-xs font-medium uppercase tracking-wider">Email Us</p>
                  <a href="mailto:triplea@tripleabooking.com" className="text-white hover:text-gold transition-colors text-sm font-semibold" data-testid="contact-email">
                    triplea@tripleabooking.com
                  </a>
                </div>

                {/* WhatsApp */}
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-green-400" />
                  </div>
                  <p className="text-white/50 text-xs font-medium uppercase tracking-wider">WhatsApp</p>
                  <a href="https://api.whatsapp.com/send?phone=61401050457&text=Hi%2C%20I%20need%20help%20with%20travel%20services" target="_blank" rel="noopener noreferrer">
                    <Button className="bg-green-600 hover:bg-green-700 text-white rounded-full px-6 h-9 text-sm font-semibold shadow-lg" data-testid="whatsapp-btn">
                      Chat on WhatsApp
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="flex justify-center mt-10">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white border border-white/30 hover:bg-white/10 rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2 transition-all" data-testid="back-to-dashboard-bottom">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
